import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { BASE_URL_API, BUCKET_DEVICES } from "../Constants";
import DataGridTable from "../Components/DataGridTable";
import CustomDatePicker from "../Components/CustomDatePicker";
import dayjs from "dayjs";
import moment from 'moment';
import Spinner from '../Components/Spinner';
import { useAuth } from "../hooks/AuthProvider";

import { LanguageContext } from "../context/LanguageContext";
import notifTranslations from "../languages/notification.json";
import { getErrorMessage } from '../utils/errorUtils';
import Alert from '../Components/Alert';
import './Notifications.css';


export const Notifications = () => {

	const { org, permissions } = useAuth();

	const canViewNotifications = permissions['view_notification'];

	const { lang } = useContext(LanguageContext);
	const tPage = notifTranslations[lang].notifications;
	const isRtl = ['ar', 'ur'].includes(lang);

	const initialRange = {
		tstart: dayjs().subtract(1, 'day').unix(),
		tstop: dayjs().unix(),
		inputValue: 1,
		timeUnit: 'day'
	};
	const [data, setData] = useState({ rows: [] });
	const [loading, setLoading] = useState(false);
	const timeRange = useRef(initialRange);
	const [alertState, setAlertState] = useState({
		open: false,
		message: '',
		type: 'info',
		position: isRtl ? 'left' : 'right'
	});

	const showAlert = (type, message) => {
		setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
	};

	const datasource = BASE_URL_API + '/data/query';
	const fetchData = useCallback(async () => {

		if (!canViewNotifications) {
			setLoading(false);
			setData({ rows: [] });
			return;
		}

		setLoading(true);
		try {
			if (!org?.id) {
				setData({ rows: [] });
				return;
			}

			const res = await axios.get(datasource, {
				params: {
					bucket: BUCKET_DEVICES,
					measurement: "ORG" + org.id,
					tstart: timeRange.current.tstart,
					tstop: timeRange.current.tstop
				}
			});

			if (res.data && Array.isArray(res.data.data)) {
				const rawRows = res.data.data;


const mappedRows = rawRows.map((r, idx) => ({
  rowId: `${r._time || "t"}-${idx}`, 
  _time: r._time,
  device: r.id ?? r.device ?? "",
  message: r.description ?? r.message ?? "",
  severity: r.severity ?? "info",
  source: r.trigger ?? r.source ?? "",
}));

setData({ rows: mappedRows });

			} else {
				console.error("Received unexpected data format:", res.data);
				setData({ rows: [] });
				showAlert('error', 'Received an invalid format from the server.');
			}

		} catch (err) {
			const errorMessage = getErrorMessage(err);
			showAlert('error', `Failed to load notifications: ${errorMessage}`);
			setData({ rows: [] });
		} finally {
			setLoading(false);
		}

	}, [datasource, org?.id, isRtl, canViewNotifications]);

	const handleTimeRange = useCallback((startEpochTime, endEpochTime) => {
		timeRange.current = {
			tstart: startEpochTime,
			tstop: endEpochTime
		};
		fetchData();
	}, [fetchData]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const columns = [
		{
			field: '_time',
			headerName: tPage.columns.time,
			type: 'dateTime',
			valueFormatter: (params) => moment(params.value).format('DD/MM/YYYY HH:mm:ss'),
			headerAlign: 'center'
		},
		{ field: 'device', headerName: tPage.columns.device, headerAlign: 'center' },
		{ field: 'message', headerName: tPage.columns.message, headerAlign: 'center' },
		{ field: 'severity', headerName: tPage.columns.severity, headerAlign: 'center' },
		{ field: 'source', headerName: tPage.columns.source, headerAlign: 'center' },
	];
	const rows = data.rows;
	const pagingoptions = [10, 20, 50, 100];
	const idcolumn = 'rowId';
	const sortField = '_time';
	const sortType = 'desc';

	if (!canViewNotifications) {
		return (
			<div className="grid-container notif-permission-denied">
				<h2>Permission Denied</h2>
				<p>You do not have the required permission to view notifications.</p>
			</div>
		);
	}

	return (
		<div className="grid-container">
			<Alert
				open={alertState.open}
				close={() => setAlertState({ ...alertState, open: false })}
				message={alertState.message}
				type={alertState.type}
				position={alertState.position}
			/>
			<div>
				<CustomDatePicker handleTimeRange={handleTimeRange} initialRange={initialRange} isLoading={loading} />
			</div>
			{loading ?
				<Spinner />
				: rows.length > 0 ?
					<div className="table-wrapper notif-table-wrapper">
						<DataGridTable
							rows={rows}
							columns={columns}
							idcolumn={idcolumn}
							pagingoptions={pagingoptions}
							sortField={sortField}
							sortType={sortType}
							showToolbar={true}
							tableKey='notifications'
						/>
					</div>
					: <div className="notif-no-data">
						{tPage.noData}
					</div>
			}
		</div>
	);
};

